import { useBreakpoints } from '@/lib/utils'
import setWindowSettings from '@/assets/ts/setting'
import { getSiteBaseUrl, getMcrRootUrl } from '@/lib/siteBaseUrl'

const { isLargeOld, isTabletLandscape } = useBreakpoints()

// work around for auto hoist script
setWindowSettings()
interface IProductSharingData {
	isLogin: boolean
	userId: string
	apiUrl: string
	PSHeader: Record<string, string>
}
export type ProductSharingUrlType =
	| 'post'
	| 'share-post'
	| 'profile'
	| 'search'
	| 'discover'
	| 'explore'
	| 'explore-express-checkout'
	| 'private-profile'
	| 'private-index'
	| 'not-found'
	| 'express-checkout'
	| 'referral'
	| 'recommended'
	| 'hot'
	| 'deals'
	| 'beauty'
	| 'deluxe'
	| 'home'
	| 'electronic'
	| 'campaign_1'
	| 'campaign_2'
	| 'campaign_3'

interface ProductSharingUrlPayload {
	id?: string
	name?: string
	fromPostId?: string
	value?: string
}

export interface IProductSharingSettings {
	accept_return_refund: boolean
	payment_methods: string[]
	discount: string[]
	banned_remarks: {
		[key: string]: string
	}
	min_create_post_days: number
	cloudflare_image_variants: {
		[key: number]: string
	}
	secondary_tabs_visibility: {
		[key: string]: boolean
	}
}
export const productSharingPostPerPage = {
	index: 20,
	explore: 20,
	productSharingIndex: () => (isTabletLandscape.value ? 20 : 12),
	discover: 10,
	profile: 10,
	search: isLargeOld.value ? 20 : 10,
	follow: 10,
	recommend: 10,
}

export const ProductSharingImageSize = {
	Profile: 100,
	ProfileMain: 200,
	PostDesktop: 800,
	PostMobile: 500,
	PostList: 300,
	RecommendUserPost: 100,
	CheckoutDesktop: 500,
	CheckoutMobile: 300,
}

export const productSharingPostColumn = {
	SizeLG: 5,
	SizeMDL: 4,
	SizeMDP: 3,
	SizeSM: 2,
}

export const productSharingData: IProductSharingData = {
	isLogin: !!window.BUYANDSHIP.settings.user_id,
	userId:
		window.BUYANDSHIP.settings && window.BUYANDSHIP.settings.user_id
			? window.BUYANDSHIP.settings.user_id
			: '',
	apiUrl: window.BUYANDSHIP.settings.productsharing_api_host,
	PSHeader: { 'Accept-Language': window.BUYANDSHIP.settings.current_language },
}

export function checkProductSharingEnable() {
	return !!(productSharingData.apiUrl && productSharingData.apiUrl !== '')
}

export function checkExpressCheckoutEnable() {
	return !!(
		window.BUYANDSHIP.settings.productsharing_express_checkout_host &&
		window.BUYANDSHIP.settings.productsharing_express_checkout_host !== ''
	)
}

export function getProductSharingUrl(
	type: ProductSharingUrlType,
	payload?: ProductSharingUrlPayload,
) {
	let fromPostIdQuery = ''
	let searchQuery = ''

	if (type === 'post' && payload && payload.fromPostId) {
		if (import.meta.env.PUBLIC_ENVIRONMENT === 'production') {
			fromPostIdQuery = `?fromPostId=${payload.fromPostId}`
		} else {
			fromPostIdQuery = `&fromPostId=${payload.fromPostId}`
		}
	}

	if (type === 'profile' && payload && payload.fromPostId) {
		if (import.meta.env.PUBLIC_ENVIRONMENT === 'production') {
			fromPostIdQuery = `?fromPostId=${payload.fromPostId}`
		} else {
			fromPostIdQuery = `&fromPostId=${payload.fromPostId}`
		}
	}

	if (type === 'search' && payload) {
		searchQuery = `?${payload.name}=${payload.value}`
	}

	if (type === 'explore' && payload) {
		searchQuery = `?${payload.name}=${payload.value}`
	}

	const profileName = encodeURIComponent(payload?.name || '')

	if (type === 'post')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/posts/${payload?.id}/${fromPostIdQuery}`
			: `/product-sharing-post?id=${payload?.id}${fromPostIdQuery}`
	if (type === 'share-post')
		return `${getSiteBaseUrl()}/sharing/posts/${payload?.id}/`
	if (type === 'profile')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/users/${profileName}/${fromPostIdQuery}`
			: `product-sharing-profile?user=${profileName}${fromPostIdQuery}`
	if (type === 'search')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/search/${searchQuery}`
			: `/product-sharing-search/${searchQuery}`
	if (type === 'recommended')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/discover/recommended`
			: '/product-sharing-index?mode=recommended'
	if (type === 'hot')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/discover/hot`
			: '/product-sharing-index?mode=hot'
	if (type === 'deals')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/mall/deals`
			: '/product-sharing-index?mode=deals'
	if (type === 'beauty')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/mall/beauty`
			: '/product-sharing-index?mode=beauty'
	if (type === 'deluxe')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/mall/deluxe`
			: '/product-sharing-index?mode=deluxe'
	if (type === 'home')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/mall/home`
			: '/product-sharing-index?mode=home'
	if (type === 'electronic')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/mall/electronic`
			: '/product-sharing-index?mode=electronic'
	if (type === 'campaign_1')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/mall/campaign_1`
			: '/product-sharing-index?mode=campaign_1'
	if (type === 'campaign_2')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/mall/campaign_2`
			: '/product-sharing-index?mode=campaign_2'
	if (type === 'campaign_3')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/mall/campaign_3`
			: '/product-sharing-index?mode=campaign_3'
	if (type === 'private-profile')
		return `${getMcrRootUrl()}/account/productsharing/posts/`
	if (type === 'private-index')
		return `${getMcrRootUrl()}/account/productsharing/posts/?create_profile=true`
	if (type === 'referral') return `${getMcrRootUrl()}/account/referral/`
	if (type === 'not-found') return import.meta.env.PROD ? '/404/' : '/not-found'
	if (type === 'express-checkout')
		return import.meta.env.PROD
			? `${getSiteBaseUrl()}/sharing/posts/${payload?.id}/checkout`
			: `checkout?postId=${payload?.id}`
	return ''
}

const timeUnits = {
	year: 1000 * 60 * 60 * 24 * 365,
	month: 1000 * 60 * 60 * 24 * 30,
	day: 1000 * 60 * 60 * 24,
	hour: 1000 * 60 * 60,
	minute: 1000 * 60,
	second: 1000,
}

type Units = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second'

const units: Units[] = ['year', 'month', 'day', 'hour', 'minute', 'second']

export function getTimeAgoValue(
	time: number,
	textObj: {
		year: string
		month: string
		day: string
		hour: string
		minute: string
		second: string
	},
) {
	if (!time) return -1

	const currentTimestamp = new Date().getTime()
	const postTimeStamp = time * 1000
	const difference = currentTimestamp - postTimeStamp

	const result = units
		.map(unit => {
			const unitDifferenceFloor = Math.floor(difference / timeUnits[unit])
			const unitDifferenceRound = Math.round(difference / timeUnits[unit])

			return { unit, unitDifferenceFloor, unitDifferenceRound }
		})
		.find(
			({ unitDifferenceFloor, unitDifferenceRound }) =>
				unitDifferenceFloor && unitDifferenceRound > 0,
		)

	return result ? `${result.unitDifferenceRound} ${textObj[result.unit]}` : -1
}

export function kFormatter(val: number, textObj: any) {
	const isChinese =
		window.BUYANDSHIP.settings.current_language.indexOf('en_') === -1
	const THOUSAND = 1000
	const TEN_THOUSAND = 10000
	const LARGE_NUMBER_THRESHOLD = isChinese ? TEN_THOUSAND : THOUSAND
	const textPrefix = isChinese
		? textObj.format.tenThousand
		: textObj.format.thousand
	const adjustedValue = isChinese ? val * 10 : val
	if (adjustedValue < LARGE_NUMBER_THRESHOLD) return val

	return `${Math.floor((val / LARGE_NUMBER_THRESHOLD) * 10) / 10}${textPrefix}`
}

interface IProductSharingPostDetail {
	id: string
	status: string
	title: string
	can_express_checkout: boolean
	category: string
	content: string
	created_at: number
	discounted: string
	hashtags: string[]
	media: {
		100: string
		200: string
		300: string
		500: string
		800: string
		1200: string
		1400: string
		content: string
		cover: string
		has_resize: boolean
		id: string
		media_type: string
		sort_order: number
		status: string
		type: string
	}[]
	media_ratio: string
	order_id: string
	original_price: string
	payment_method: string
	user: {
		username: string
		image: string
		total_contributions: number
		member_types: string[]
		is_followed: boolean
	}
	item_name: string
	item_url: string
	item_unit_price: string
	item_weight: string
	is_bookmarked: boolean
	is_liked: boolean
	ec_product_name: string
	ec_product_slug: string
	ec_local_price: string
	ec_price: string
	ec_fulfillment_mode: string
	ec_fulfillment_month: string | number
	ec_fulfillment_range: string
	ec_fulfillment_max_order_per_user: number
	ec_fulfillment_max_quantity: number
	ec_stock_status: string
	like_count: number
	service: string
	site: string
	interest: number
	warehouse: string
	publish_date: number
}

export function setPostDetails(data: IProductSharingPostDetail) {
	return {
		id: data.id,
		status: data.status,
		title: data.title,
		content: data.content,
		hashtagInfo: data.hashtags,
		media: data.media,
		mediaRatio: data.media_ratio,
		userInfo: {
			name: data.user.username,
			image: data.user.image,
			contributions: data.user.total_contributions,
			memberTypes: data.user.member_types,
		},
		itemInfo: {
			name: data.item_name,
			ecName: data.ec_product_name,
			ecLocalPrice: data.ec_local_price,
			ecPrice: data.ec_price,
			url: data.item_url,
			category: data.category,
			originalPrice: data.original_price,
			unitPrice: data.item_unit_price,
			weight: data.item_weight,
			orderId: data.order_id,
			warehouse: data.warehouse,
			paymentMethod: data.payment_method,
			discounted: data.discounted.replace('%', ''),
			service: data.service,
			location: data.site,
		},
		interest: data.interest,
		isLiked: data.is_liked,
		isBookmarked: data.is_bookmarked,
		isFollowed: data.user.is_followed,
		likeCount: data.like_count,
		createdAt: data.created_at || 0,
		productSlug: data.ec_product_slug,
		publishDate: data.publish_date || 0,
		isExpressCheckout: data.can_express_checkout,
		warehouse: data.warehouse,
		ecFulfillmentMode: data.ec_fulfillment_mode,
		ecFulfillmentMonth: data.ec_fulfillment_month,
		ecFulfillmentRange: data.ec_fulfillment_range,
		ecFulfillmentMaxOrderPerUser: data.ec_fulfillment_max_order_per_user,
		ecFulfillmentMaxQuantity: data.ec_fulfillment_max_quantity,
		ecStockStatus: data.ec_stock_status,
	}
}

export enum MemberTypes {
	Official1 = 'MEMBER_TYPE_OFFICIAL_1',
	Assigned1 = 'MEMBER_TYPE_ASSIGNED_1',
	Assigned2 = 'MEMBER_TYPE_ASSIGNED_2',
}

interface Mapping {
	name: string
	ga: string
	api: string
}

export const postListModeMappings: Record<string, Mapping> = {
	recommended: {
		name: 'recommended',
		ga: 'Index',
		api: 'discover',
	},
	hot: {
		name: 'hot',
		ga: 'Hot Topic',
		api: 'hot_topic',
	},
	deals: {
		name: 'deals',
		ga: 'Express checkout',
		api: 'express_checkout_groupbuy',
	},
	beauty: {
		name: 'beauty',
		ga: 'Beauty',
		api: 'express_checkout_groupbuy_makeup',
	},
	deluxe: {
		name: 'deluxe',
		ga: 'Luxury',
		api: 'express_checkout_luxury',
	},
	home: {
		name: 'home',
		ga: 'Living',
		api: 'express_checkout_daily_products',
	},
	electronic: {
		name: 'electronic',
		ga: 'Electronic',
		api: 'express_checkout_electronic',
	},
	campaign_1: {
		name: 'campaign_1',
		ga: 'Campaign 1',
		api: 'express_checkout_other_1',
	},
	campaign_2: {
		name: 'campaign_2',
		ga: 'Campaign 2',
		api: 'express_checkout_other_2',
	},
	campaign_3: {
		name: 'campaign_3',
		ga: 'Campaign 3',
		api: 'express_checkout_other_3',
	},
}

export function getPostListModeMapping(
	category: string,
): Record<string, string> {
	const groupValues: Record<string, string> = {}
	for (const mapping of Object.values(postListModeMappings)) {
		if (category in mapping) {
			groupValues[mapping.name] = mapping[category as keyof Mapping]
		}
	}
	return groupValues
}
